import { Table, Card, Empty, Pagination, Spin } from "antd";
import type { ColumnGroupType, ColumnType, ColumnsType } from "antd/es/table";
import type { SortOrder as AntdSortOrder } from "antd/es/table/interface";
import { isValidElement, useMemo, type ReactNode } from "react";
import { columnHeaderText, columnValue } from "../helpers/csv";

export type SortOrder = "asc" | "desc";

interface ResponsiveTableProps<T> {
  columns: ColumnsType<T>;
  dataSource: T[];
  loading?: boolean;
  rowKey?: string;
  onRowClick?: (record: T) => void;
  renderCard?: (record: T) => ReactNode;
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number, pageSize: number) => void;
  };
  /**
   * Server-side sorting. Columns opt in with `sorter: true`; their key (explicit `key`, else
   * `dataIndex`) is what goes to the API, so it has to match that endpoint's applySort
   * allowlist. Client-side `sorter` functions would only reorder the page on screen, which on
   * a paginated list is a lie — hence `sorter: true` everywhere.
   */
  sort?: {
    key: string;
    order: SortOrder | "";
    onChange: (key: string | undefined, order: SortOrder | undefined) => void;
  };
}

function columnKeyOf<T>(col: ColumnType<T> | ColumnGroupType<T>): string | undefined {
  if (col.key != null) return String(col.key);
  const dataIndex = (col as ColumnType<T>).dataIndex;
  if (dataIndex == null) return undefined;
  return Array.isArray(dataIndex) ? dataIndex.join(".") : String(dataIndex);
}

/** antd lets a render return `{ children, props }` to set colSpan/rowSpan; a card wants the children. */
function cellContent(rendered: unknown): ReactNode {
  if (rendered != null && typeof rendered === "object" && !isValidElement(rendered) && !Array.isArray(rendered)) {
    return "children" in rendered ? ((rendered as { children?: ReactNode }).children ?? null) : null;
  }
  return rendered as ReactNode;
}

/**
 * The card a list gets when its page hasn't written a `renderCard`: the same columns the table
 * would show, one label/value line each, so a page is never blank on a phone. A page with a
 * better shape for its rows still passes `renderCard` and this is not used.
 */
function defaultCardFields<T>(columns: ColumnsType<T>, record: T, index: number): ReactNode[] {
  const fields: ReactNode[] = [];
  columns.forEach((col, i) => {
    const children = (col as ColumnGroupType<T>).children;
    if (children?.length) {
      fields.push(...defaultCardFields(children, record, index));
      return;
    }
    const c = col as ColumnType<T>;
    const raw = columnValue(record, c.dataIndex);
    const content = cellContent(
      typeof c.render === "function" ? c.render(raw, record, index) : typeof raw === "boolean" ? (raw ? "Yes" : "No") : raw,
    );
    if (content == null || content === "" || content === false) return;
    // A column with no header is a row action; it keeps its cell but gets no label.
    const label = columnHeaderText(col);
    fields.push(
      <div className="glut-card-field" key={columnKeyOf(col) ?? i}>
        {label && <span className="glut-card-label">{label}</span>}
        <span className="glut-card-value">{content}</span>
      </div>,
    );
  });
  return fields;
}

// Full table >=768px; card list on phones (site staff are on phones — architecture.md §11).
export function ResponsiveTable<T extends object>({
  columns,
  dataSource,
  loading,
  rowKey = "id",
  onRowClick,
  renderCard,
  pagination,
  sort,
}: ResponsiveTableProps<T>) {
  // The sort arrows are driven off the URL, not the table's own state, so a shared link or a
  // reload shows the ordering the rows are actually in.
  const columnsWithSort = useMemo(() => {
    if (!sort) return columns;
    return columns.map((col) => {
      if (!col.sorter) return col;
      const key = columnKeyOf(col);
      const active = key != null && key === sort.key;
      const sortOrder: AntdSortOrder = active ? (sort.order === "desc" ? "descend" : "ascend") : null;
      return { ...col, sortOrder };
    });
  }, [columns, sort]);

  return (
    <>
      <div className="glut-table-view">
        <Table<T>
          columns={columnsWithSort}
          dataSource={dataSource}
          loading={loading}
          rowKey={rowKey}
          size="middle"
          pagination={false}
          scroll={{ x: "max-content" }}
          onChange={
            sort
              ? (_p, _f, sorter) => {
                  const active = Array.isArray(sorter) ? sorter[0] : sorter;
                  const key = active?.columnKey ?? active?.field;
                  // Third click clears the sorter — fall back to the endpoint's default order.
                  if (!active?.order || key == null) sort.onChange(undefined, undefined);
                  else sort.onChange(String(key), active.order === "descend" ? "desc" : "asc");
                }
              : undefined
          }
          onRow={onRowClick ? (record) => ({ onClick: () => onRowClick(record), style: { cursor: "pointer" } }) : undefined}
        />
      </div>
      <div className="glut-card-list">
        {/* The table's own spinner is in the hidden half of the layout, so phones need their own. */}
        {loading && dataSource.length === 0 && <Spin style={{ margin: "24px auto" }} />}
        {!loading && dataSource.length === 0 && <Empty />}
        {dataSource.map((record, index) => (
          <Card
            key={(record as unknown as Record<string, unknown>)[rowKey] as string}
            size="small"
            onClick={onRowClick ? () => onRowClick(record) : undefined}
            style={onRowClick ? { cursor: "pointer" } : undefined}
          >
            {renderCard ? renderCard(record) : defaultCardFields(columns, record, index)}
          </Card>
        ))}
      </div>
      {pagination && (
        <div style={{ marginTop: 16, textAlign: "right" }}>
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={pagination.onChange}
            showSizeChanger
          />
        </div>
      )}
    </>
  );
}
